// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Control components for asset waterfall report.
 *
 * Provides toggle controls for switching between units/dollars view
 * using toggle button groups.
 */
import {
  AttachMoneyOutlined,
  CurrencyPoundOutlined,
  EuroOutlined,
  Inventory2Outlined,
  MonetizationOnOutlined,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { RollforwardWaterfallDisplayMetricType } from '@/report/types';
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

/** Toggle noun per app-allowed currency; other codes fall back to the ISO code */
const CURRENCY_TOGGLE_NOUNS: Partial<Record<IsoCurrencyCode, string>> = {
  USD: 'Dollars',
  CAD: 'Dollars',
  GBP: 'Pounds',
  EUR: 'Euros',
};

/** Toggle icon per app-allowed currency; other codes fall back to a generic money glyph */
const CURRENCY_TOGGLE_ICONS: Partial<
  Record<IsoCurrencyCode, typeof AttachMoneyOutlined>
> = {
  USD: AttachMoneyOutlined,
  CAD: AttachMoneyOutlined,
  GBP: CurrencyPoundOutlined,
  EUR: EuroOutlined,
};

interface Props {
  metricType: RollforwardWaterfallDisplayMetricType;
  onMetricTypeChange: (
    metricType: RollforwardWaterfallDisplayMetricType
  ) => void;
  /** Currency the monetary toggle reflects (icon + noun); defaults to USD */
  currencyCode?: IsoCurrencyCode;
}

export function AssetWaterfallControls(props: Props) {
  const currencyCode = props.currencyCode ?? 'USD';
  const currencyNoun = CURRENCY_TOGGLE_NOUNS[currencyCode] ?? currencyCode;
  const CurrencyIcon =
    CURRENCY_TOGGLE_ICONS[currencyCode] ?? MonetizationOnOutlined;
  const handleMetricTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    value: RollforwardWaterfallDisplayMetricType
  ) => {
    if (value !== null) {
      props.onMetricTypeChange(value);
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <ToggleButtonGroup
        value={props.metricType}
        exclusive
        onChange={handleMetricTypeChange}
        size="small"
      >
        <ToggleButton value="units">
          <Stack direction="row" alignItems="center" spacing={1} paddingX={1}>
            <Inventory2Outlined fontSize="small" />
            <Typography variant="button" textTransform="none">
              Units
            </Typography>
          </Stack>
        </ToggleButton>
        <ToggleButton value="dollars">
          <Stack direction="row" alignItems="center" spacing={1} paddingX={1}>
            <CurrencyIcon fontSize="small" />
            <Typography variant="button" textTransform="none">
              {currencyNoun}
            </Typography>
          </Stack>
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
}
